/**
 * Manage OAuth authentication flow in a popup window.
 *
 * This hook handles the complete OAuth lifecycle:
 * 1. Opens a popup window for OAuth authentication
 * 2. Monitors the window to detect when it's closed by the user
 * 3. Provides methods to close the window programmatically
 * 4. Cleans up intervals and window references on unmount
 *
 * The hook polls the window's closed status every 100ms and calls the
 * onWindowClosed callback when detected, allowing the parent component
 * to handle the cancellation gracefully.
 *
 * @param {Function} onWindowClosed - Callback invoked when user closes the OAuth window
 * @returns {Object} Object containing:
 *   - authUrl: Current OAuth authorization URL (null when not active)
 *   - connectionId: Current connection ID being authenticated
 *   - openOAuthWindow: Function to start OAuth flow (url, connectionId)
 *   - closeOAuthWindow: Function to close the OAuth window and clean up
 */
import { useCallback, useEffect, useRef, useState } from "react";

export function useOAuthFlow(onWindowClosed) {
  const [authUrl, setAuthUrl] = useState(null);
  const [connectionId, setConnectionId] = useState(null);
  const oauthWindowRef = useRef();
  const checkIntervalRef = useRef();

  // Close window and clear intervals on unmount
  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
        oauthWindowRef.current.close();
      }
    };
  }, []);

  /**
   * Handles cleanup when the OAuth window is closed by the user.
   * Resets state, clears intervals, and invokes the parent callback.
   */
  const handleWindowClosed = useCallback(() => {
    setAuthUrl(null);
    setConnectionId(null);
    if (oauthWindowRef.current) {
      oauthWindowRef.current = undefined;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = undefined;
    }
    onWindowClosed?.();
  }, [onWindowClosed]);

  /**
   * Opens a popup window for OAuth authentication and starts monitoring it.
   *
   * The window is centered on the screen and sized appropriately for OAuth flows.
   * We poll every 100ms to detect if the user closes the window manually.
   *
   * @param {string} url - OAuth authorization URL
   * @param {string|number} connId - Connection ID for this OAuth flow
   */
  const openOAuthWindow = useCallback(
    (url, connId) => {
      setAuthUrl(url);
      setConnectionId(connId);

      // Center the popup window on the screen
      const left = window.screen.width / 2 - 320;
      const top = window.screen.height / 2 - 300;
      oauthWindowRef.current = window.open(
        url,
        "_blank",
        `width=650, height=600, top=${top}, left=${left}`
      );

      // Poll every 100ms to detect if user closes the window
      checkIntervalRef.current = setInterval(() => {
        if (oauthWindowRef.current?.closed) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = undefined;
          handleWindowClosed();
        }
      }, 100);
    },
    [handleWindowClosed]
  );

  const closeOAuthWindow = useCallback(() => {
    if (oauthWindowRef.current) {
      oauthWindowRef.current.close();
      oauthWindowRef.current = undefined;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = undefined;
    }
    setAuthUrl(null);
    setConnectionId(null);
  }, []);

  return {
    authUrl,
    connectionId,
    openOAuthWindow,
    closeOAuthWindow,
  };
}
